import type {
  EligibilityItem,
  EligibilityResult,
  RequestState,
} from '../../api/user/eligibility.api';

/** Câu nhắc dưới danh sách điều kiện, giữ đúng nguyên văn đã chốt. */
export const ELIGIBILITY_REMINDER =
  'Các điều kiện trên giúp Cab duyệt nhanh hơn, bạn chưa cần đạt đủ hết mới được gửi. Đội ngũ Cab xem từng hồ sơ và có thể duyệt sớm cho những kênh có tiềm năng. Cũng có lúc Cab chưa duyệt được vì kênh chưa hợp với định hướng nội dung hiện tại, khi đó Cab sẽ báo lý do để bạn chuẩn bị cho lần sau. Riêng xác minh danh tính eKYC là bắt buộc với mọi tài khoản.';

export const REQUIRED_GROUP_TITLE = 'Bắt buộc';
export const REVIEW_GROUP_TITLE = 'Điều kiện xét duyệt';
export const PENDING_TEXT = 'Cab đang xem yêu cầu của bạn.';
export const EKYC_NEEDS_EMAIL_TEXT = 'Bạn cần xác minh email trước rồi mới xác minh danh tính được.';
export const EKYC_NEEDS_PHONE_TEXT =
  'Bạn cần xác minh số điện thoại trước rồi mới xác minh danh tính được.';
export const APPROVED_DONATE_OFF_TEXT =
  'Yêu cầu của bạn đã được duyệt trước đây. Bạn hãy liên hệ hỗ trợ để mở lại nhận donate.';
export const APPROVED_CREATOR_OFF_TEXT =
  'Yêu cầu của bạn đã được duyệt trước đây. Bạn hãy liên hệ hỗ trợ để mở lại tích xanh creator.';
export const RESUBMIT_LABEL = 'Gửi lại yêu cầu';

export const splitGroups = (items: EligibilityItem[] | null | undefined) => {
  const list = items ?? [];
  return {
    required: list.filter((i) => i.required),
    review: list.filter((i) => !i.required),
  };
};

export const itemStatusText = (met: boolean | null | undefined): string =>
  met === true ? 'Đã đạt' : met === false ? 'Chưa đạt' : 'Chưa kiểm được';

/** "{hiện tại} trên {cần}" khi máy chủ trả đủ hai số và mục chưa đạt (đã đạt thì "18 trên 7" chỉ gây rối). */
export const progressText = (item: EligibilityItem): string | null => {
  if (item.met === true) return null;
  if (item.current === null || item.current === undefined) return null;
  if (item.target === null || item.target === undefined) return null;
  return `${item.current.toLocaleString('vi-VN')} trên ${item.target.toLocaleString('vi-VN')}`;
};

/** Đọc mốc giờ máy chủ trả. Chuỗi không ghi múi giờ thì coi là UTC (máy chủ luôn trả UTC). */
export const parseUtc = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(iso.trim());
  const t = Date.parse(hasZone ? iso : `${iso}Z`);
  return Number.isNaN(t) ? null : t;
};

const VN_OFFSET_MS = 7 * 60 * 60 * 1000;
const pad = (n: number) => String(n).padStart(2, '0');

/** Ngày dd/MM/yyyy theo giờ Việt Nam (UTC+7, không có giờ mùa hè). */
export const formatVnDate = (iso: string | null | undefined): string | null => {
  const t = parseUtc(iso);
  if (t === null) return null;
  const d = new Date(t + VN_OFFSET_MS);
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
};

const stripEndDot = (s: string) => s.trim().replace(/[.。]+$/, '');

/**
 * Câu trong khung trạng thái yêu cầu. Chưa gửi thì không hiện gì.
 * Đã duyệt: khung điều kiện chỉ hiện cho người chưa nhận được donate (hoặc chưa có tích xanh), tức là admin đã
 * tắt lại sau khi duyệt; máy chủ không nhận gửi lại, nên chỉ người dùng sang hỗ trợ (truyền `kind` để chọn câu).
 */
export const requestStateText = (
  request: RequestState | null | undefined,
  kind?: 'donate' | 'creator'
): string | null => {
  if (!request) return null;
  if (request.status === 'pending') return PENDING_TEXT;
  if (request.status === 'approved') {
    if (kind === 'donate') return APPROVED_DONATE_OFF_TEXT;
    if (kind === 'creator') return APPROVED_CREATOR_OFF_TEXT;
    return null;
  }
  if (request.status !== 'rejected') return null;
  const reason = request.rejectReason ? stripEndDot(request.rejectReason) : '';
  // Máy chủ lưu lý do dạng "Chữ của mã" hoặc "Chữ của mã. Ghi thêm" (lý do khác chỉ có chữ admin ghi).
  const head = reason
    ? `Cab chưa duyệt yêu cầu của bạn. Lý do: ${reason}.`
    : 'Cab chưa duyệt yêu cầu của bạn.';
  const date = formatVnDate(request.canResubmitAt);
  return date
    ? `${head} Bạn có thể gửi lại từ ngày ${date}.`
    : `${head} Bạn có thể gửi lại yêu cầu ngay bây giờ.`;
};

/** Trạng thái yêu cầu có cho gửi (hoặc gửi lại) lúc này không. */
export const requestAllowsSubmit = (
  request: RequestState | null | undefined,
  now: number = Date.now()
): boolean => {
  const status = request?.status ?? 'none';
  if (status === 'none') return true;
  if (status !== 'rejected') return false;
  const at = parseUtc(request?.canResubmitAt);
  return at === null || now >= at;
};

export interface SubmitState {
  enabled: boolean;
  label: string;
  /** Đã có yêu cầu đang chờ hoặc đã duyệt: ẩn biểu mẫu gửi. */
  hidden: boolean;
}

export const submitState = (
  result: EligibilityResult | null | undefined,
  defaultLabel: string,
  now: number = Date.now()
): SubmitState => {
  if (!result) return { enabled: false, label: defaultLabel, hidden: false };
  const status = result.request?.status ?? 'none';
  const allowed = requestAllowsSubmit(result.request, now);
  return {
    enabled: !!result.requiredMet && allowed,
    label: status === 'rejected' && allowed ? RESUBMIT_LABEL : defaultLabel,
    hidden: status === 'pending' || status === 'approved',
  };
};

/**
 * Nút eKYC chỉ bật khi email đã xác minh, và số điện thoại đã xác minh nếu mục đó có trong danh sách.
 * Trả null khi được bấm, ngược lại trả câu giải thích.
 */
export const ekycBlockedReason = (items: EligibilityItem[] | null | undefined): string | null => {
  const list = items ?? [];
  const email = list.find((i) => i.key === 'email');
  if (email && email.met !== true) return EKYC_NEEDS_EMAIL_TEXT;
  const phone = list.find((i) => i.key === 'phone');
  if (phone && phone.met !== true) return EKYC_NEEDS_PHONE_TEXT;
  return null;
};

/** Nhãn các mục bắt buộc chưa đạt (met=false; chưa kiểm được thì máy chủ không chặn). */
export const unmetRequiredLabels = (result: EligibilityResult | null | undefined): string[] =>
  (result?.items ?? []).filter((i) => i.required && i.met === false).map((i) => i.label);

/** Nhãn các mục xét duyệt chưa đạt hoặc chưa kiểm được: admin cần ghi lý do duyệt ngoại lệ. */
export const unmetReviewLabels = (result: EligibilityResult | null | undefined): string[] =>
  (result?.items ?? []).filter((i) => !i.required && i.met !== true).map((i) => i.label);
